import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { addDays, addMonths, format, parseISO, startOfISOWeek } from 'date-fns';
import { Area, AreaChart, CartesianGrid, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { AlertCircle, BookOpen, Calculator, CalendarPlus, ChevronLeft, ChevronRight, Info, Palmtree, Scale, TrendingUp } from 'lucide-react';
import { api } from '../lib/api';
import { cn, formatDate } from '../lib/utils';
import { Card, Disclosure, EmptyState, IconButton, Kpi, PageHeader, PageSkeleton } from '../components/ui';

/** Duur van een vakantiedag zonder ingeroosterde dienst (CAO art 59 lid 1c, bij 36u/week). */
const UREN_PER_VRIJE_DAG = 7.2;
/** Opbouw per feestdag op een werkdag (zie urenbijlages: +7,2u bovenwettelijk). */
const FEESTDAG_OPBOUW = 7.2;
/** Zoveel periodes toont de tabel standaard. */
const PERIODES_KORT = 6;

const h = (n: number) => `${n < 0 ? '−' : ''}${Math.abs(n).toFixed(1).replace('.', ',')}u`;
const d = (n: number) => `${(n / 8).toFixed(1).replace('.', ',')} dagen`;
const neg = (n: number) => (n < 0 ? 'text-red-300' : undefined);

export function VerlofPage() {
  const today = format(new Date(), 'yyyy-MM-dd');
  const horizon = format(addMonths(new Date(), 15), 'yyyy-MM-dd');

  const { data: hist, isLoading, error } = useQuery({ queryKey: ['leave-history'], queryFn: () => api.leave.history() });
  const snaps = hist?.snapshots ?? [];
  const last = snaps[snaps.length - 1];

  const { data: shifts = [] } = useQuery({
    queryKey: ['shifts', last?.snapshot_date, horizon],
    queryFn: () => api.shifts.list(last!.snapshot_date, horizon),
    enabled: !!last,
  });
  const { data: periods = [] } = useQuery({
    queryKey: ['periods', last?.snapshot_date, horizon],
    queryFn: () => api.shifts.periods(last!.snapshot_date, horizon),
    enabled: !!last,
  });
  const year = new Date().getFullYear();
  const { data: hol1 = [] } = useQuery({ queryKey: ['holidays', year], queryFn: () => api.cao.holidays(year) });
  const { data: hol2 = [] } = useQuery({ queryKey: ['holidays', year + 1], queryFn: () => api.cao.holidays(year + 1) });

  // Opbouw per periode: wettelijk zoals op de laatste urenbijlage, bovenwettelijk de basis
  // zonder feestdagen (laagste positieve waarde van de laatste 6 periodes).
  const opbouw = useMemo(() => {
    if (!last) return { wett: 0, boven: 0 };
    const recent = snaps.slice(-6).map((s) => s.bovenwettelijk_opbouw ?? 0).filter((v) => v > 0);
    return { wett: last.wettelijk_opbouw ?? 0, boven: recent.length ? Math.min(...recent) : 0 };
  }, [snaps, last]);

  /** Verwacht saldo op `date` (vóór verlof op die dag), uitgaande van de laatste urenbijlage. */
  function projected(date: string): { saldo: number; opgebouwd: number; gepland: number } {
    if (!last) return { saldo: 0, opgebouwd: 0, gepland: 0 };
    const start = (last.wettelijk_saldo ?? 0) + (last.bovenwettelijk_saldo ?? 0) + (last.feestdagverlof_saldo ?? 0);
    const n = periods.filter((p) => p.end_date > last.snapshot_date && p.end_date < date).length;
    const feestdagen = [...hol1, ...hol2].filter((x) => {
      const wd = parseISO(x.holiday_date).getDay();
      return x.holiday_date > last.snapshot_date && x.holiday_date < date && wd >= 1 && wd <= 5;
    }).length;
    const opgebouwd = n * (opbouw.wett + opbouw.boven) + feestdagen * FEESTDAG_OPBOUW;
    const gepland = shifts
      .filter((s) => s.leave_type === 'vakantie' && s.shift_date > last.snapshot_date && s.shift_date < date)
      .reduce((sum, s) => sum + (s.total_hours ?? 0), 0);
    return { saldo: start + opgebouwd - gepland, opgebouwd, gepland };
  }

  if (isLoading) return <PageSkeleton kpis={4} />;

  if (error || !last) {
    return (
      <div className="page">
        <PageHeader title="Verlof" />
        <Card>
          {error ? (
            <EmptyState icon={AlertCircle} title="Je verlof kon niet laden" text={(error as Error).message} />
          ) : (
            <EmptyState icon={Palmtree} title="Nog geen verlofsaldo" text="Dat komt van de urenbijlage bij je loonstrook." />
          )}
        </Card>
      </div>
    );
  }

  const nu = projected(today);
  const perPeriode = opbouw.wett + opbouw.boven;
  const periodesTotNul = nu.saldo < 0 && perPeriode > 0 ? Math.ceil(-nu.saldo / perPeriode) : 0;
  const nulPeriode = periodesTotNul ? periods.filter((p) => p.end_date >= today)[periodesTotNul - 1] : undefined;
  const bijlage = `Urenbijlage periode ${last.period_number}`;

  return (
    <div className="page">
      <PageHeader title="Verlof" sub="Je saldo volgens de laatste urenbijlage, plus wat je sindsdien opbouwt en opneemt." />

      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        <Kpi icon={Palmtree} label="Saldo vandaag" value={nu.saldo} format={h} rows={[['In dagen', d(nu.saldo).replace(' dagen', '')]]} />
        <Kpi icon={Scale} label="Wettelijk" value={last.wettelijk_saldo ?? 0} format={h} rows={[['Vervalt na', '2 jaar']]} />
        <Kpi
          icon={Scale}
          label="Bovenwettelijk"
          value={last.bovenwettelijk_saldo ?? 0}
          format={h}
          rows={[
            ['Vervalt na', '5 jaar'],
            !!last.feestdagverlof_saldo && ['Feestdagen', h(last.feestdagverlof_saldo)],
          ]}
        />
        <Kpi
          icon={TrendingUp}
          label="Opbouw per periode"
          value={perPeriode}
          format={h}
          rows={[
            ['Wettelijk', h(opbouw.wett)],
            ['Bovenwettelijk', h(opbouw.boven)],
            ['Feestdag', `+${h(FEESTDAG_OPBOUW)}`],
          ]}
        />
      </div>

      <div className="grid gap-3 sm:gap-4 lg:grid-cols-5 [&>*]:min-w-0">
        <div className="lg:col-span-3">
          <Simulatie projected={projected} shifts={shifts} today={today} />
        </div>
        <Card title="Zo is je saldo berekend" icon={Calculator} className="lg:col-span-2">
          <dl className="list -mx-4 sm:-mx-5">
            <Line label={`${bijlage} (${formatDate(last.snapshot_date)})`} value={h((last.wettelijk_saldo ?? 0) + (last.bovenwettelijk_saldo ?? 0) + (last.feestdagverlof_saldo ?? 0))} />
            <Line label="Opgebouwd sindsdien" value={`+${h(nu.opgebouwd)}`} />
            {nu.gepland > 0 && <Line label="Verlof sindsdien" value={h(-nu.gepland)} />}
            <Line label="Saldo vandaag" value={h(nu.saldo)} strong valueClass={neg(nu.saldo)} />
          </dl>
          <p className="mt-3 text-sm text-slate-400">
            Je verlof bestaat uit twee potjes. Wettelijk zijn de 20 dagen per jaar die je volgens de wet krijgt;
            bovenwettelijk zijn de 4 extra dagen uit de CAO (art. 59). Samen 24 dagen per jaar, {h(opbouw.wett)} wettelijk
            en {h(opbouw.boven)} bovenwettelijk per periode. Staat een potje in de min, dan heb je daaruit meer opgenomen dan je had opgebouwd;
            dat loop je in met nieuwe opbouw.
            {periodesTotNul > 0 && (
              <>
                {' '}Zonder nieuw verlof sta je over {periodesTotNul} periodes weer op nul
                {nulPeriode && <> (rond {formatDate(nulPeriode.end_date)})</>}.
              </>
            )}
          </p>
        </Card>
      </div>

      <Prognose start={nu.saldo} vanaf={today} perPeriode={perPeriode} periodEnds={periods.map((p) => p.end_date)} />

      <PerPeriode snaps={snaps} />

      <Card flush className="min-w-0">
        <div className="list">
          <Disclosure icon={Info} title="Uitleg september 2026" meta="40 uur vakantieverlof van 11 t/m 15 september">
            <div className="space-y-2 text-sm text-slate-300">
              <p>
                Je vijf diensten van 11 t/m 15 september zijn als vakantieverlof van je saldo gegaan (verzoek en akkoord per mail op
                7 september). Dat was de reis naar de begrafenis van je oma.
              </p>
              <p className="text-slate-400">
                Bij een grootouder geeft de CAO 1 dag betaald verlof op de dag van de begrafenis, alleen als je die dag moest werken
                (art. 63). Op donderdag 17 september stond je niet ingeroosterd, dus de boeking klopt.
              </p>
            </div>
          </Disclosure>
          <Disclosure icon={BookOpen} title="Regels voor verlof aanvragen" meta="CAO art. 59, 60 en arbeidsvoorwaardenreglement">
            <ul className="list-disc space-y-2 pl-5 text-sm text-slate-300">
              <li>Een vakantiedag duurt zo lang als de geplande dienst. Staat er niets gepland, dan is het 7,2 uur (art. 59).</li>
              <li>Trigion reageert binnen 7 dagen op 1 dag verlof en binnen 1 maand op meer dagen. Geen reactie? Dan mag je het verlof opnemen (art. 60 lid 6).</li>
              <li>Zomervakantie (30 april tot 1 oktober) die je vóór 1 januari aanvraagt, beantwoordt Trigion vóór 31 januari.</li>
              <li>Een negatief saldo mag volgens de CAO; Trigion beslist na overleg (art. 60 lid 1). De CAO regelt niet dat te veel opgenomen uren bij uitdiensttreding verrekend worden; het reglement dat dat zegt is van Trigion Safety en geldt niet voor jou.</li>
            </ul>
          </Disclosure>
        </div>
      </Card>
    </div>
  );
}

function Line({ label, value, strong, valueClass }: { label: string; value: string; strong?: boolean; valueClass?: string }) {
  return (
    <div className="row">
      <dt className={cn('min-w-0 flex-1 truncate text-sm', strong ? 'font-medium text-slate-100' : 'text-slate-400')}>{label}</dt>
      <dd className={cn('t-num shrink-0 text-sm', strong ? 'font-semibold text-slate-50' : 'text-slate-200', valueClass)}>{value}</dd>
    </div>
  );
}

type Snapshot = {
  year: number;
  period_number: number;
  snapshot_date: string;
  wettelijk_saldo: number | null;
  bovenwettelijk_saldo: number | null;
  feestdagverlof_saldo: number | null;
  wettelijk_opbouw: number | null;
  bovenwettelijk_opbouw: number | null;
};

/** Historie per periode: standaard de laatste zes, de rest na een klik. */
function PerPeriode({ snaps }: { snaps: Snapshot[] }) {
  const [alles, setAlles] = useState(false);
  const rows = useMemo(() => {
    const rev = [...snaps].reverse();
    return rev.map((s, i) => {
      const prev = rev[i + 1];
      const tot = (s.wettelijk_saldo ?? 0) + (s.bovenwettelijk_saldo ?? 0) + (s.feestdagverlof_saldo ?? 0);
      const prevTot = prev ? (prev.wettelijk_saldo ?? 0) + (prev.bovenwettelijk_saldo ?? 0) + (prev.feestdagverlof_saldo ?? 0) : 0;
      const opb = (s.wettelijk_opbouw ?? 0) + (s.bovenwettelijk_opbouw ?? 0);
      return { s, tot, opb, opgenomen: Math.max(0, prevTot + opb - tot) };
    });
  }, [snaps]);
  const shown = alles ? rows : rows.slice(0, PERIODES_KORT);

  return (
    <Card
      flush
      title="Per periode"
      className="min-w-0"
      action={
        rows.length > PERIODES_KORT && (
          <button type="button" className="btn-ghost btn-sm" onClick={() => setAlles(!alles)}>
            {alles ? 'Minder tonen' : `Alle ${rows.length} periodes tonen`}
          </button>
        )
      }
    >
      <div className="overflow-x-auto">
        <table className="table whitespace-nowrap">
          <thead>
            <tr>
              <th>Periode</th>
              <th className="r">Opbouw</th>
              <th className="r">Opgenomen</th>
              <th className="r hidden sm:table-cell">Wettelijk</th>
              <th className="r hidden sm:table-cell">Bovenwettelijk</th>
              <th className="r">Saldo</th>
            </tr>
          </thead>
          <tbody>
            {shown.map(({ s, tot, opb, opgenomen }) => (
              <tr key={`${s.year}-${s.period_number}`}>
                <td>
                  <div className="text-slate-200">
                    P{s.period_number} {s.year}
                  </div>
                  <div className="t-num text-xs text-slate-500">{formatDate(s.snapshot_date)}</div>
                </td>
                <td className="r text-slate-300">{opb < 0 ? h(opb) : `+${h(opb)}`}</td>
                <td className={cn('r', opgenomen > 0.05 ? 'text-slate-100' : 'text-slate-600')}>
                  {opgenomen > 0.05 ? h(-opgenomen) : '–'}
                </td>
                <td className={cn('r hidden text-slate-400 sm:table-cell', neg(s.wettelijk_saldo ?? 0))}>{h(s.wettelijk_saldo ?? 0)}</td>
                <td className={cn('r hidden text-slate-400 sm:table-cell', neg(s.bovenwettelijk_saldo ?? 0))}>{h(s.bovenwettelijk_saldo ?? 0)}</td>
                <td className={cn('r font-medium text-slate-100', neg(tot))}>{h(tot)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="border-t border-surface-line/60 px-4 py-3 text-xs text-slate-400 sm:px-5">
        Opgenomen = vorig saldo + opbouw − nieuw saldo.
      </p>
    </Card>
  );
}

/**
 * Verlof uitrekenen: blader per week door je rooster en tik de diensten aan die je vrij wilt.
 * Verlof kost de duur van de dienst (CAO art 59 lid 1d), dus je rooster is de makkelijkste invoer.
 */
function Simulatie({
  projected,
  shifts,
  today,
}: {
  projected: (date: string) => { saldo: number };
  shifts: Array<{ id: number; shift_date: string; start_time: string; end_time: string; total_hours: number; leave_type: string | null }>;
  today: string;
}) {
  const [gekozen, setGekozen] = useState<Set<number>>(new Set());
  const [weekIdx, setWeekIdx] = useState(0);

  // Weken (maandag) met minstens één werkdienst vanaf vandaag.
  const weken = useMemo(() => {
    const map = new Map<string, typeof shifts>();
    for (const s of shifts) {
      if (s.shift_date < today || (s.leave_type && s.leave_type !== 'work')) continue;
      const ma = format(startOfISOWeek(parseISO(s.shift_date)), 'yyyy-MM-dd');
      map.set(ma, [...(map.get(ma) ?? []), s]);
    }
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [shifts, today]);

  const toggle = (id: number) =>
    setGekozen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const keuze = shifts.filter((s) => gekozen.has(s.id));
  const uren = keuze.reduce((sum, s) => sum + (s.total_hours ?? 0), 0);
  const eerste = keuze.map((s) => s.shift_date).sort()[0];
  const voor = eerste ? projected(eerste).saldo : null;

  const idx = Math.min(weekIdx, Math.max(0, weken.length - 1));
  const week = weken[idx];
  const dagen = week ? Array.from({ length: 7 }, (_, i) => format(addDays(parseISO(week[0]), i), 'yyyy-MM-dd')) : [];
  const perWeek = (ma: string) => (weken.find(([w]) => w === ma)?.[1] ?? []).filter((s) => gekozen.has(s.id)).length;

  return (
    <Card
      title="Verlof uitrekenen"
      icon={CalendarPlus}
      className="h-full"
      action={
        gekozen.size > 0 && (
          <button type="button" className="text-xs text-slate-400 hover:text-slate-100" onClick={() => setGekozen(new Set())}>
            Wissen
          </button>
        )
      }
    >
      {!week ? (
        <p className="text-sm text-slate-500">Er staan nog geen diensten in je rooster.</p>
      ) : (
        <>
          <div className="mb-3 flex items-center justify-between gap-2">
            <IconButton icon={ChevronLeft} label="Vorige week" onClick={() => setWeekIdx(idx - 1)} disabled={idx === 0} />
            <div className="min-w-0 text-center">
              <div className="text-sm font-medium text-slate-100">Week {format(parseISO(week[0]), 'I')}</div>
              <div className="t-num text-xs text-slate-400">
                {formatDate(week[0], 'd MMM')} t/m {formatDate(dagen[6], 'd MMM')}
                {perWeek(week[0]) > 0 && <span className="text-brand-300"> · {perWeek(week[0])} gekozen</span>}
              </div>
            </div>
            <IconButton icon={ChevronRight} label="Volgende week" onClick={() => setWeekIdx(idx + 1)} disabled={idx >= weken.length - 1} />
          </div>

          <div className="grid grid-cols-7 gap-1.5">
            {dagen.map((d) => {
              const dienst = week[1].find((s) => s.shift_date === d);
              const aan = !!dienst && gekozen.has(dienst.id);
              return (
                <button
                  key={d}
                  type="button"
                  disabled={!dienst}
                  aria-pressed={dienst ? aan : undefined}
                  aria-label={dienst ? `${formatDate(d, 'EEEE d MMMM')} ${dienst.start_time.slice(0, 5)}–${dienst.end_time.slice(0, 5)}` : undefined}
                  onClick={() => dienst && toggle(dienst.id)}
                  className={cn(
                    'flex min-h-[64px] flex-col items-center justify-center rounded-lg border px-1 py-1.5 text-center transition-colors duration-fast ease-calm',
                    !dienst && 'border-transparent text-slate-600',
                    dienst && !aan && 'border-surface-line text-slate-200 hover:border-slate-500',
                    aan && 'border-brand-400 bg-brand-400/15 text-slate-50'
                  )}
                >
                  <span className="text-xs text-slate-400">{formatDate(d, 'EEEEEE')}</span>
                  <span className="t-num text-sm font-medium">{formatDate(d, 'd')}</span>
                  <span className="t-num mt-0.5 text-[11px] leading-tight text-slate-400">
                    {dienst ? `${dienst.start_time.slice(0, 5)}` : '–'}
                  </span>
                </button>
              );
            })}
          </div>
        </>
      )}

      <dl className="list -mx-4 mt-4 border-t border-surface-line sm:-mx-5">
        {voor != null ? (
          <>
            <Line label={`Gekozen: ${keuze.length} ${keuze.length === 1 ? 'dienst' : 'diensten'}`} value={h(uren)} />
            <Line label={`Saldo op ${formatDate(eerste!, 'dd-MM')}`} value={h(voor)} valueClass={neg(voor)} />
            <Line label="Saldo na dit verlof" value={h(voor - uren)} strong valueClass={neg(voor - uren)} />
          </>
        ) : (
          week && <Line label="Tik een dienst aan om te zien wat het kost" value="–" />
        )}
      </dl>
    </Card>
  );
}

/**
 * Saldo-prognose: verwacht saldo aan het eind van elke periode, zonder nieuw verlof, tot je weer
 * boven nul staat. Na het laatst bekende periode-einde telt elke 28 dagen als een periode.
 */
function Prognose({
  start,
  vanaf,
  perPeriode,
  periodEnds,
}: {
  start: number;
  vanaf: string;
  perPeriode: number;
  periodEnds: string[];
}) {
  const punten = useMemo(() => {
    const ends = periodEnds.filter((e) => e > vanaf);
    let laatste = ends[ends.length - 1] ?? vanaf;
    const out: Array<{ datum: string; label: string; saldo: number }> = [
      { datum: vanaf, label: formatDate(vanaf, 'dd-MM'), saldo: Math.round(start * 10) / 10 },
    ];
    let saldo = start;
    for (let i = 0; i < 40 && (saldo < 0 || out.length < 4) && perPeriode > 0; i++) {
      let e = ends[i];
      if (!e) {
        laatste = format(addDays(parseISO(laatste), 28), 'yyyy-MM-dd');
        e = laatste;
      }
      saldo += perPeriode;
      out.push({ datum: e, label: formatDate(e, 'd MMM'), saldo: Math.round(saldo * 10) / 10 });
    }
    return out;
  }, [start, vanaf, perPeriode, periodEnds]);

  const positief = punten.find((p) => p.saldo >= 0);
  return (
    <Card title="Wanneer sta je weer positief?" icon={TrendingUp}>
      <p className="-mt-1 mb-3 text-sm text-slate-300">
        {start >= 0 ? (
          'Je saldo is al positief.'
        ) : positief ? (
          <>
            Zonder nieuw verlof sta je rond <span className="font-medium text-slate-50">{formatDate(positief.datum)}</span> weer boven nul.
          </>
        ) : (
          'Met deze opbouw kom je de komende drie jaar niet boven nul.'
        )}
        <span className="text-slate-400"> Opbouw {h(perPeriode)} per periode.</span>
      </p>
      <div className="-mx-2 h-44">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={punten} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
            <CartesianGrid stroke="#2A2F37" vertical={false} />
            <XAxis dataKey="label" tick={{ fill: '#8A919C', fontSize: 12 }} tickLine={false} axisLine={false} interval="preserveStartEnd" />
            <YAxis tick={{ fill: '#8A919C', fontSize: 12 }} tickLine={false} axisLine={false} width={44} tickFormatter={(v: number) => `${v}u`} />
            <Tooltip
              contentStyle={{ background: '#1A1F26', border: '1px solid #252B33', borderRadius: 8, fontSize: 13 }}
              labelStyle={{ color: '#E8EAED' }}
              labelFormatter={(_, p) => (p?.[0] ? formatDate((p[0].payload as { datum: string }).datum) : '')}
              formatter={(v: number) => [h(v), 'Saldo']}
            />
            <ReferenceLine y={0} stroke="#5CCB9A" strokeDasharray="4 3" />
            <Area type="monotone" dataKey="saldo" stroke="#CED2D8" strokeWidth={2} fill="#CED2D8" fillOpacity={0.06} isAnimationActive={false} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}

function Stat({ label, value, sub, neg: isNeg }: { label: string; value: string; sub?: string; neg?: boolean }) {
  return (
    <div className="min-w-0">
      <div className="t-label truncate">{label}</div>
      <div className={cn('t-num mt-1 text-base font-semibold', isNeg ? 'text-red-300' : 'text-slate-100')}>{value}</div>
      {sub && <div className="mt-0.5 text-xs text-slate-400">{sub}</div>}
    </div>
  );
}
